import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { RefreshCcw, UploadCloud, Download, X } from "lucide-react";
import CatalogFilters from "../components/common/CatalogFilters";
import CatalogTable from "../components/common/CatalogTable";
import ItemCostsEditor from "../components/common/ItemCostsEditor";
import ItemFlagsEditor from "../components/common/ItemFlagsEditor";
import ItemPriceEditor from "../components/common/ItemPriceEditor";
import ItemInventoryEditor from "../components/common/ItemInventoryEditor";
import AdvancedImportModal from "../components/common/AdvancedImportModal";
import GettingStartedGuide from "../components/common/GettingStartedGuide";
import BackfillFeesModal from "../components/walmart/BackfillFeesModal";
import ItemFieldsEditor from "../components/common/ItemFieldsEditor";
import {
    listCatalog, listCostDefinitions, createCostDefinition, listFlagDefinitions, listFieldDefinitions, getUnpublishedReasons,
    getMissingFeeItems, getLivePriceIncentives,
} from "../services/catalogService";
import SkuDetailPage from "./reports/SkuDetailPage";
import { resolveWalmartItemId } from "../services/wfsShipmentService";

// "123456789" or "https://www.walmart.com/ip/some-name/123456789?..." -> "123456789"
const parseWalmartItemId = (raw) => {
    const s = (raw || "").trim();
    const m = s.match(/walmart\.com\/ip\/(?:[^/?#]+\/)?(\d+)/i);
    if (m) return m[1];
    return /^\d{5,}$/.test(s) ? s : null;
};

const CatalogPage = ({ itemSyncStatus, itemSyncPopup, setItemSyncPopup, inventorySyncStatus }) => {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const [marketplace, setMarketplace] = useState("");
    const [search, setSearch] = useState("");
    const [availability, setAvailability] = useState("");
    const [publishedStatus, setPublishedStatus] = useState("PUBLISHED");
    const [unpublishedReasonGroups, setUnpublishedReasonGroups] = useState(() => new Set());
    const [unpublishedReasonOptions, setUnpublishedReasonOptions] = useState([]);
    const [activeFlags, setActiveFlags] = useState([]);
    const [brand, setBrand] = useState("");
    const [notesFilter, setNotesFilter] = useState("");
    const [priceMin, setPriceMin] = useState("");
    const [priceMax, setPriceMax] = useState("");
    const [sfStockMin, setSfStockMin] = useState("");
    const [sfStockMax, setSfStockMax] = useState("");
    const [wfsStockMin, setWfsStockMin] = useState("");
    const [wfsStockMax, setWfsStockMax] = useState("");
    const [sortBy, setSortBy] = useState("updated_at");
    const [sortDir, setSortDir] = useState("desc");

    // Numeric walmart.com item ID / product URL in the search box -> search by its SKU instead.
    const [resolvedSearch, setResolvedSearch] = useState(null); // { q, sku }
    useEffect(() => {
        const itemId = parseWalmartItemId(search);
        if (!itemId) { setResolvedSearch(null); return; }
        let cancelled = false;
        const t = setTimeout(() => {
            resolveWalmartItemId(itemId)
                .then((d) => { if (!cancelled) setResolvedSearch(d?.match?.sku ? { q: search, sku: d.match.sku } : null); })
                .catch(() => { if (!cancelled) setResolvedSearch(null); });
        }, 400);
        return () => { cancelled = true; clearTimeout(t); };
    }, [search]);
    const effectiveSearch = resolvedSearch && resolvedSearch.q === search ? resolvedSearch.sku : search;

    const AUTO_SCROLL_KEY = "msp_catalog_autoscroll_enabled";
    const [autoScrollEnabled, setAutoScrollEnabledState] = useState(() => {
        try {
            const stored = localStorage.getItem(AUTO_SCROLL_KEY);
            return stored === null ? false : stored === "true"; // default false
        } catch {
            return false;
        }
    });
    const setAutoScrollEnabled = (value) => {
        setAutoScrollEnabledState(value);
        try { localStorage.setItem(AUTO_SCROLL_KEY, String(value)); } catch { /* ignore */ }
    };

    // Rows-per-page, persisted like autoScroll above.
    const PAGE_SIZE_KEY = "msp_catalog_page_size";
    const [pageSize, setPageSizeState] = useState(() => {
        try {
            const stored = localStorage.getItem(PAGE_SIZE_KEY);
            const n = stored ? parseInt(stored, 10) : 50;
            return [25, 50, 100, 200].includes(n) ? n : 50;
        } catch {
            return 50;
        }
    });
    const setPageSize = (value) => {
        setPageSizeState(value);
        try { localStorage.setItem(PAGE_SIZE_KEY, String(value)); } catch { /* ignore */ }
        setPage(1);
    };

    // Client-side "+ Add Filter" filters. Applied on top of whatever the
    // backend returns for the current page; loadMoreForFilters below tops
    // the page back up when filtering shrinks it below pageSize.
    const [customFilters, setCustomFilters] = useState([]);
    const [filterLoadingMore, setFilterLoadingMore] = useState(false);
    const [filterBackendExhausted, setFilterBackendExhausted] = useState(false);
    const backendPageRef = useRef(1);
    const extraPagesLoadedRef = useRef(0); // how many extra backend pages loadMoreForFilters pulled in to fill this page

    // Once-a-day check: on landing on Catalog, ask the backend whether ANY
    // items (account-wide, not just the current filter view) are still
    // missing fees, and if so, auto-open the same Backfill Fees modal the
    // manual button opens — defaulting the action to "on" rather than
    // relying on the seller to remember to check. Gated to once per
    // calendar day via localStorage so it doesn't nag on every page visit.
    const BACKFILL_CHECK_KEY = "msp_catalog_backfill_last_check";
    const BACKFILL_CHECK_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000; // once a week
    const [checkingBackfill, setCheckingBackfill] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const now = Date.now();
        let lastCheck = null;
        try { lastCheck = localStorage.getItem(BACKFILL_CHECK_KEY); } catch { /* ignore */ }
        const lastCheckMs = lastCheck ? parseInt(lastCheck, 10) : null;

        console.log(`[backfill-check] last check: ${lastCheckMs ? new Date(lastCheckMs).toISOString() : "never"}, now: ${new Date(now).toISOString()}`);

        if (lastCheckMs && (now - lastCheckMs) < BACKFILL_CHECK_INTERVAL_MS) {
            console.log(`[backfill-check] checked within the last week (${Math.round((now - lastCheckMs) / (24 * 60 * 60 * 1000))} day(s) ago) — skipping`);
            return;
        }

        console.log("[backfill-check] running daily missing-fees check with filters:", currentFilterParams);
        setCheckingBackfill(true);

        // Use the EXACT same filters object the modal itself uses
        // (currentFilterParams, passed to BackfillFeesModal as
        // filters={currentFilterParams}) — so this check and the modal's
        // own internal check always agree on what "missing" means.
        getMissingFeeItems(currentFilterParams)
            .then((d) => {
                if (cancelled) return;
                console.log("[backfill-check] result:", d);
                const needsBackfill = (d.sf_count || 0) > 0 || (d.wfs_items || []).length > 0;
                console.log(`[backfill-check] needsBackfill=${needsBackfill} (sf_count=${d.sf_count}, wfs_items=${(d.wfs_items || []).length})`);
                if (needsBackfill) setShowBackfillFees(true);
            })
            .catch((e) => {
                console.error("[backfill-check] failed:", e);
            })
            .finally(() => {
                if (!cancelled) setCheckingBackfill(false);
                try { localStorage.setItem(BACKFILL_CHECK_KEY, String(now)); } catch { /* ignore */ }
            });

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [costDefinitions, setCostDefinitions] = useState([]);
    const [flagDefinitions, setFlagDefinitions] = useState([]);
    const [fieldDefinitions, setFieldDefinitions] = useState([]);

    const [selectedIds, setSelectedIds] = useState([]);
    const [showCostsEditor, setShowCostsEditor] = useState(false);
    const [showFlagsEditor, setShowFlagsEditor] = useState(false);
    const [showFieldsEditor, setShowFieldsEditor] = useState(false);
    const [showPriceEditor, setShowPriceEditor] = useState(false);
    const [showInventoryEditor, setShowInventoryEditor] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [showBackfillFees, setShowBackfillFees] = useState(false);
    const [openSkuReportId, setOpenSkuReportId] = useState(null);

    // Identical shape to what loadCatalog already sends to listCatalog() —
    // reused so "Backfill Fees" always operates on EXACTLY what's currently
    // filtered/visible in Catalog, never the whole account's catalog.
    const currentFilterParams = {
        marketplace, search: effectiveSearch, availability, publishedStatus,
        unpublished_reason_groups: [...unpublishedReasonGroups],
        flags: activeFlags.join(","), brand, notes: notesFilter,
        price_min: priceMin, price_max: priceMax,
        sf_stock_min: sfStockMin, sf_stock_max: sfStockMax,
        wfs_stock_min: wfsStockMin, wfs_stock_max: wfsStockMax,
    };

    // ── Client-side custom filter matching ──
    const getFilterableFieldValue = (item, columnId) => {
        if (columnId === "title") return item.title;
        if (columnId === "sku") return item.sku;
        if (columnId === "upc_gtin") return item.upc || item.gtin;
        if (columnId === "supplier") return item.supplier;
        if (columnId === "notes") return item.notes;
        if (columnId === "expiration_date") return item.expiration_date;
        if (columnId === "total_fees") return item.total_fees;
        if (columnId === "estimated_profit") return item.estimated_profit;
        if (columnId === "estimated_roi") return item.estimated_roi;
        if (columnId.startsWith("cost:")) {
            const defId = columnId.slice(5);
            const found = (item.costs || []).find((c) => c.cost_definition_id === defId);
            return found ? found.value : null;
        }
        if (columnId.startsWith("field:")) {
            const defId = columnId.slice(6);
            const found = (item.fields || []).find((f) => f.field_definition_id === defId);
            return found ? found.value : null;
        }
        return null;
    };

    const itemMatchesCustomFilters = (item) => customFilters.every((f) => {
        const raw = getFilterableFieldValue(item, f.columnId);
        if (f.type === "text") {
            if (!f.value.trim()) return true;
            return String(raw || "").toLowerCase().includes(f.value.trim().toLowerCase());
        }
        if (f.type === "number_range") {
            const num = raw === null || raw === undefined || raw === "" ? null : parseFloat(raw);
            if (f.value.min !== "" && (num === null || num < parseFloat(f.value.min))) return false;
            if (f.value.max !== "" && (num === null || num > parseFloat(f.value.max))) return false;
            return true;
        }
        if (f.type === "date_range") {
            if (!raw) return f.value.from === "" && f.value.to === "";
            if (f.value.from && raw < f.value.from) return false;
            if (f.value.to && raw > f.value.to) return false;
            return true;
        }
        return true;
    });

    // Any change to custom filters invalidates the current page/buffer —
    // force back to page 1 and zero the extra-pages-loaded counter so Next
    // starts fresh under the new filter instead of jumping by a stale amount.
    const handleCustomFiltersChange = (updater) => {
        setCustomFilters(updater);
        extraPagesLoadedRef.current = 0;
        setPage(1);
    };

    const visibleItems = useMemo(
        () => (customFilters.length === 0 ? items : items.filter(itemMatchesCustomFilters)),
        [items, customFilters]
    );

    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState("");

    const itemSyncPercent = itemSyncStatus.totalItems
        ? Math.min(100, Math.round((itemSyncStatus.itemsSynced / itemSyncStatus.totalItems) * 100))
        : null;

    const inventorySyncPercent = (() => {
        const { sfProcessed, sfTotal, wfsProcessed, wfsTotal } = inventorySyncStatus;
        const knownTotal = (sfTotal ?? 0) + (wfsTotal ?? 0);
        if (!knownTotal) return null;
        const processed = Math.min(sfProcessed, sfTotal ?? sfProcessed) + Math.min(wfsProcessed, wfsTotal ?? wfsProcessed);
        return Math.min(100, Math.round((processed / knownTotal) * 100));
    })();

    const loadDefinitions = () => {
        listCostDefinitions().then(async (d) => {
            const defs = d.definitions || [];
            // "Cost Price" (Buy Cost) is treated as a first-class, always-
            // present field throughout the app (SKU Builder, batch item
            // forms, profit estimates) — make sure every account actually
            // has a real cost_definitions row for it from the moment
            // Catalog first loads, instead of only getting created lazily
            // the first time someone opens the batch item form.
            let cp = defs.find((c) => c.key === "cost_price" || c.name?.toLowerCase() === "cost price");
            if (!cp) {
                try {
                    const created = await createCostDefinition("Cost Price");
                    cp = created?.definition;
                    if (cp) defs.push(cp);
                } catch { /* non-fatal — worst case it's created lazily elsewhere as before */ }
            }
            setCostDefinitions(defs);
        });
        listFlagDefinitions().then((d) => setFlagDefinitions(d.definitions || []));
        listFieldDefinitions().then((d) => setFieldDefinitions(d.definitions || []));
        getUnpublishedReasons().then((d) => setUnpublishedReasonOptions(d.groups || []));
    };

    // CSV-safe cell: wraps in quotes and escapes embedded quotes whenever the
    // value itself contains a comma, quote, or newline — leaves simple values
    // unquoted for a cleaner-looking file.
    const csvCell = (value) => {
        if (value === null || value === undefined) return "";
        const str = String(value);
        if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
        return str;
    };

    // Pulls every page of the catalog matching the CURRENT filters (not just
    // the visible page), then builds one flat CSV with every field we have —
    // one column per cost definition and one combined "flags" column, since
    // flags are a variable-length list rather than a fixed set of columns.
    const handleExportAll = async () => {
        setExporting(true);
        setExportError("");
        try {
            const allItems = [];
            let currentPage = 1;
            const pageSize = 200;
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const d = await listCatalog({
                    marketplace, search: effectiveSearch, availability, publishedStatus,
                    unpublished_reason_groups: [...unpublishedReasonGroups],
                    flags: activeFlags.join(","), brand, notes: notesFilter,
                    price_min: priceMin, price_max: priceMax,
                    sf_stock_min: sfStockMin, sf_stock_max: sfStockMax,
                    wfs_stock_min: wfsStockMin, wfs_stock_max: wfsStockMax,
                    sort_by: sortBy, sort_dir: sortDir,
                    page: currentPage, page_size: pageSize,
                });
                const batch = d.items || [];
                allItems.push(...batch);
                if (batch.length < pageSize || allItems.length >= (d.total || 0)) break;
                currentPage += 1;
                if (currentPage > 500) break; // hard safety cap — 100k items
            }

            const headers = [
                "Marketplace", "Title", "SKU", "UPC", "GTIN", "WPID", "Brand",
                "Price", "Currency", "SF Stock", "WFS Stock", "WFS Inbound",
                "Availability", "Published Status", "Lifecycle Status",
                "Supplier", "Expiration Date", "Notes",
                ...costDefinitions.map((c) => c.name),
                "Flags", "Image URL", "Updated At",
            ];

            const rows = allItems.map((item) => {
                const costByDefId = {};
                (item.costs || []).forEach((c) => { costByDefId[c.cost_definition_id] = c.value; });
                const flagNames = (item.flags || []).map((f) => f.name).join("; ");

                return [
                    item.marketplace, item.title, item.sku, item.upc, item.gtin, item.wpid, item.brand,
                    item.price, item.currency, item.sf_stock, item.wfs_stock, item.wfs_inbound,
                    item.availability, item.published_status, item.lifecycle_status,
                    item.supplier, item.expiration_date, item.notes,
                    ...costDefinitions.map((c) => costByDefId[c.id] ?? ""),
                    flagNames, item.image_url, item.updated_at,
                ];
            });

            const csv = [headers, ...rows].map((row) => row.map(csvCell).join(",")).join("\n");
            const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" }); // BOM for Excel
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 10);
            a.href = url;
            a.download = `catalog_export_${stamp}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            setExportError(e.message || "Export failed");
        } finally {
            setExporting(false);
        }
    };

    const loadCatalog = useCallback(() => {
        setLoading(true);
        backendPageRef.current = page;
        extraPagesLoadedRef.current = 0;
        setFilterBackendExhausted(false);
        listCatalog({
            marketplace, search: effectiveSearch, availability, publishedStatus,
            unpublished_reason_groups: [...unpublishedReasonGroups],
            flags: activeFlags.join(","), brand, notes: notesFilter,
            price_min: priceMin, price_max: priceMax,
            sf_stock_min: sfStockMin, sf_stock_max: sfStockMax,
            wfs_stock_min: wfsStockMin, wfs_stock_max: wfsStockMax,
            sort_by: sortBy, sort_dir: sortDir,
            page, page_size: pageSize,
        })
            .then((d) => {
                setItems(d.items || []);
                setTotal(d.total || 0);
                if ((d.items || []).length < pageSize) setFilterBackendExhausted(true);
            })
            .finally(() => setLoading(false));
    }, [marketplace, search, effectiveSearch, availability, publishedStatus, unpublishedReasonGroups, activeFlags, brand, notesFilter,
        priceMin, priceMax, sfStockMin, sfStockMax, wfsStockMin, wfsStockMax, sortBy, sortDir, page, pageSize]);

    // Custom filters can shrink a fetched page after filtering. Rather than
    // change how pagination works, top the current page back up by pulling
    // additional backend pages (appended, not replacing) and re-filtering,
    // until pageSize filtered rows are showing or the backend runs out.
    const loadMoreForFilters = useCallback(() => {
        if (filterLoadingMore || filterBackendExhausted) return;
        setFilterLoadingMore(true);
        const nextBackendPage = backendPageRef.current + 1;
        listCatalog({
            marketplace, search: effectiveSearch, availability, publishedStatus,
            unpublished_reason_groups: [...unpublishedReasonGroups],
            flags: activeFlags.join(","), brand, notes: notesFilter,
            price_min: priceMin, price_max: priceMax,
            sf_stock_min: sfStockMin, sf_stock_max: sfStockMax,
            wfs_stock_min: wfsStockMin, wfs_stock_max: wfsStockMax,
            sort_by: sortBy, sort_dir: sortDir,
            page: nextBackendPage, page_size: pageSize,
        })
            .then((d) => {
                const batch = d.items || [];
                backendPageRef.current = nextBackendPage;
                extraPagesLoadedRef.current += 1;
                setItems((prev) => [...prev, ...batch]);
                if (batch.length < pageSize) setFilterBackendExhausted(true);
            })
            .finally(() => setFilterLoadingMore(false));
    }, [marketplace, search, effectiveSearch, availability, publishedStatus, unpublishedReasonGroups, activeFlags, brand, notesFilter,
        priceMin, priceMax, sfStockMin, sfStockMax, wfsStockMin, wfsStockMax, sortBy, sortDir, pageSize, filterLoadingMore, filterBackendExhausted]);

    // Auto top-up: while custom filters are on and the page is short, fetch more.
    useEffect(() => {
        if (customFilters.length === 0) return;
        if (loading || filterLoadingMore || filterBackendExhausted) return;
        if (visibleItems.length < pageSize) loadMoreForFilters();
    }, [customFilters, visibleItems.length, pageSize, loading, filterLoadingMore, filterBackendExhausted, loadMoreForFilters]);

    const [incentivesBySku, setIncentivesBySku] = useState({});

    useEffect(() => {
        loadDefinitions();
        loadCatalog();
        getLivePriceIncentives()
            .then((d) => setIncentivesBySku(d.by_sku || {}))
            .catch(() => { /* non-fatal — column just stays empty */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { const t = setTimeout(loadCatalog, 300); return () => clearTimeout(t); }, [loadCatalog]);

    const toggleSelect = (id) => setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
    const toggleSelectAll = () => setSelectedIds((s) => (s.length === items.length ? [] : items.map((i) => i.id)));
    const toggleFlag = (name) => setActiveFlags((f) => (f.includes(name) ? f.filter((x) => x !== name) : [...f, name]));

    // Column header click -> asc on first click, desc on second, back to the
    // default (updated_at desc) on a third click of the same column.
    const handleSortToggle = (columnId) => {
        setPage(1);
        if (sortBy !== columnId) {
            setSortBy(columnId);
            setSortDir("desc"); // first click = biggest/newest first
        } else if (sortDir === "desc") {
            setSortDir("asc");
        } else {
            setSortBy("updated_at");
            setSortDir("desc");
        }
    };

    return (
        <div className="max-w-7xl mx-auto py-8 px-6 mb-24">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Catalog</h1>
                    <p className="text-gray-500 text-sm">{total} product{total !== 1 && "s"} across all connected marketplaces</p>
                </div>

                <div className="flex items-center gap-2">
                    <GettingStartedGuide short={true} />
                    <button
                        onClick={handleExportAll}
                        disabled={exporting}
                        title="Exports every item matching your current filters"
                        className="flex items-center gap-2 px-5 py-3 bg-white border-2 border-gray-200 text-gray-600 rounded-xl font-bold text-sm hover:bg-gray-50 transition disabled:opacity-50"
                    >
                        <Download size={16} className={exporting ? "animate-pulse" : ""} />
                        {exporting ? "Exporting..." : "Export Catalog"}
                    </button>
                    <button
                        onClick={() => setShowBackfillFees(true)}
                        title={checkingBackfill ? "Checking whether any items need fee backfilling..." : "Only processes items matching your current filters"}
                        className="flex items-center gap-2 px-5 py-3 bg-white border-2 border-gray-200 text-gray-600 rounded-xl font-bold text-sm hover:bg-gray-50 transition"
                    >
                        {checkingBackfill && <RefreshCcw size={14} className="animate-spin" />}
                        Backfill Fees
                    </button>
                    <button
                        onClick={() => setShowImport(true)}
                        className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-xl font-bold text-sm shadow-lg hover:opacity-90 hover:shadow-xl transition"
                    >
                        <UploadCloud size={18} /> Advanced Import
                    </button>
                </div>
            </div>

            {exportError && (
                <div className="mb-4 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    Export failed: {exportError}
                </div>
            )}

            {itemSyncStatus.running && (
                <div className="mb-4">
                    <div className="text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 flex items-center gap-2 mb-1">
                        <RefreshCcw size={12} className="animate-spin" />
                        Syncing catalog — {itemSyncStatus.itemsSynced}{itemSyncStatus.totalItems ? `/${itemSyncStatus.totalItems}` : ""} items
                        {itemSyncPercent !== null && ` (${itemSyncPercent}%)`}
                    </div>
                    {itemSyncPercent !== null && (
                        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 transition-all" style={{ width: `${itemSyncPercent}%` }} />
                        </div>
                    )}
                </div>
            )}

            {itemSyncPopup && (
                <div className="fixed top-20 right-6 z-50 bg-white border border-teal-200 shadow-2xl rounded-xl px-4 py-3 flex items-center gap-3">
                    <span className="text-sm text-gray-700">Catalog sync complete — {itemSyncStatus.itemsSynced} item(s) synced.</span>
                    <button
                        onClick={() => { setItemSyncPopup(false); loadCatalog(); }}
                        className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-bold hover:bg-teal-700 transition"
                    >
                        Click to reload
                    </button>
                    <button onClick={() => setItemSyncPopup(false)} className="text-gray-400 hover:text-gray-600">
                        <X size={14} />
                    </button>
                </div>
            )}
            {itemSyncStatus.error && (
                <div className="mb-4 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    Catalog sync error: {itemSyncStatus.error}
                </div>
            )}

            {inventorySyncStatus.running && (
                <div className="mb-4">
                    <div className="text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 flex items-center gap-2 mb-1">
                        <RefreshCcw size={12} className="animate-spin" />
                        Auto-syncing inventory — SF: {inventorySyncStatus.sfProcessed}{inventorySyncStatus.sfTotal ? `/${inventorySyncStatus.sfTotal}` : ""}
                        {" · "}WFS: {inventorySyncStatus.wfsProcessed}{inventorySyncStatus.wfsTotal ? `/${inventorySyncStatus.wfsTotal}` : ""}
                    </div>
                    {inventorySyncPercent !== null && (
                        <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div className="h-full bg-teal-500 transition-all" style={{ width: `${inventorySyncPercent}%` }} />
                        </div>
                    )}
                </div>
            )}
            {inventorySyncStatus.error && (
                <div className="mb-4 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    Inventory sync error: {inventorySyncStatus.error}
                </div>
            )}

            <CatalogFilters
                marketplace={marketplace} setMarketplace={(v) => { setMarketplace(v); setPage(1); }}
                search={search} setSearch={(v) => { setSearch(v); setPage(1); }}
                availability={availability} setAvailability={(v) => { setAvailability(v); setPage(1); }}
                publishedStatus={publishedStatus} setPublishedStatus={(v) => { setPublishedStatus(v); setPage(1); }}
                unpublishedReasonGroupOptions={unpublishedReasonOptions}
                unpublishedReasonGroups={unpublishedReasonGroups} setUnpublishedReasonGroups={(v) => { setUnpublishedReasonGroups(v); setPage(1); }}
                flagDefinitions={flagDefinitions} activeFlags={activeFlags} toggleFlag={toggleFlag}
                brand={brand} setBrand={(v) => { setBrand(v); setPage(1); }}
                notesFilter={notesFilter} setNotesFilter={(v) => { setNotesFilter(v); setPage(1); }}
                priceMin={priceMin} setPriceMin={(v) => { setPriceMin(v); setPage(1); }}
                priceMax={priceMax} setPriceMax={(v) => { setPriceMax(v); setPage(1); }}
                sfStockMin={sfStockMin} setSfStockMin={(v) => { setSfStockMin(v); setPage(1); }}
                sfStockMax={sfStockMax} setSfStockMax={(v) => { setSfStockMax(v); setPage(1); }}
                wfsStockMin={wfsStockMin} setWfsStockMin={(v) => { setWfsStockMin(v); setPage(1); }}
                wfsStockMax={wfsStockMax} setWfsStockMax={(v) => { setWfsStockMax(v); setPage(1); }}
                autoScrollEnabled={autoScrollEnabled} setAutoScrollEnabled={setAutoScrollEnabled}
                customFilters={customFilters} setCustomFilters={handleCustomFiltersChange}
                costDefinitions={costDefinitions} fieldDefinitions={fieldDefinitions}
            />

            {customFilters.length > 0 && (
                <div className="mb-3 text-xs text-gray-400 flex items-center gap-2">
                    Showing {visibleItems.length} matching row{visibleItems.length === 1 ? "" : "s"} on this page after custom filters
                    {filterLoadingMore && <span className="text-teal-500">· loading more to fill the page...</span>}
                    {!filterLoadingMore && filterBackendExhausted && visibleItems.length < pageSize && <span>· no more matches</span>}
                </div>
            )}

            {loading ? (
                <div className="text-center py-16 text-gray-400 text-sm">Loading catalog...</div>
            ) : (
                <CatalogTable
                    items={visibleItems} costDefinitions={costDefinitions} flagDefinitions={flagDefinitions} fieldDefinitions={fieldDefinitions}
                    incentivesBySku={incentivesBySku}
                    selectedIds={selectedIds} toggleSelect={toggleSelect} toggleSelectAll={toggleSelectAll}
                    onEditCosts={() => setShowCostsEditor(true)} onEditFlags={() => setShowFlagsEditor(true)}
                    onEditFields={() => setShowFieldsEditor(true)}
                    onEditPrice={() => setShowPriceEditor(true)} onEditInventory={() => setShowInventoryEditor(true)}
                    sortBy={sortBy} sortDir={sortDir} onSortToggle={handleSortToggle}
                    autoScrollEnabled={autoScrollEnabled}
                    onOpenSkuReport={setOpenSkuReportId}
                />
            )}

            <div className="flex items-center justify-center gap-4 mt-5 flex-wrap">
                {total > pageSize && (
                    <div className="flex items-center gap-3">
                        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                            className="px-4 py-2 text-sm border border-gray-200 rounded-lg disabled:opacity-30">Previous</button>
                        <span className="text-sm text-gray-500">Page {page} of {Math.ceil(total / pageSize)}</span>
                        <button
                            onClick={() => {
                                const jump = 1 + extraPagesLoadedRef.current;
                                extraPagesLoadedRef.current = 0;
                                setPage((p) => p + jump);
                            }}
                            disabled={page >= Math.ceil(total / pageSize)}
                            className="px-4 py-2 text-sm border border-gray-200 rounded-lg disabled:opacity-30">Next</button>
                    </div>
                )}
                <label className="flex items-center gap-2 text-sm text-gray-500">
                    Rows per page
                    <select value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-teal-500 bg-white">
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                    </select>
                </label>
            </div>

            {showCostsEditor && (
                <ItemCostsEditor selectedIds={selectedIds} costDefinitions={costDefinitions}
                    onClose={() => setShowCostsEditor(false)}
                    onDone={() => { setShowCostsEditor(false); setSelectedIds([]); loadCatalog(); }} />
            )}

            {showFlagsEditor && (
                <ItemFlagsEditor selectedIds={selectedIds} flagDefinitions={flagDefinitions}
                    onClose={() => setShowFlagsEditor(false)}
                    onDone={() => { setShowFlagsEditor(false); setSelectedIds([]); loadCatalog(); }} />
            )}

            {showFieldsEditor && (
                <ItemFieldsEditor selectedIds={selectedIds} fieldDefinitions={fieldDefinitions}
                    onClose={() => setShowFieldsEditor(false)}
                    onDone={() => { setShowFieldsEditor(false); setSelectedIds([]); loadCatalog(); }} />
            )}

            {showPriceEditor && (
                <ItemPriceEditor selectedIds={selectedIds}
                    onClose={() => setShowPriceEditor(false)}
                    onDone={() => { setShowPriceEditor(false); setSelectedIds([]); loadCatalog(); }} />
            )}

            {showInventoryEditor && (
                <ItemInventoryEditor selectedIds={selectedIds}
                    onClose={() => setShowInventoryEditor(false)}
                    onDone={() => { setShowInventoryEditor(false); setSelectedIds([]); loadCatalog(); }} />
            )}

            {showImport && (
                <AdvancedImportModal marketplace={marketplace || "walmart"}
                    onClose={() => { setShowImport(false); loadCatalog(); loadDefinitions(); }}
                    onImported={() => { }} />
            )}

            {showBackfillFees && (
                <BackfillFeesModal
                    filters={currentFilterParams}
                    onClose={() => setShowBackfillFees(false)}
                    onDone={() => loadCatalog()}
                />
            )}

            {openSkuReportId && (
                <div
                    className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                    onClick={() => setOpenSkuReportId(null)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl overflow-y-auto"
                        style={{ width: "70vw", height: "80vh" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <SkuDetailPage catalogItemId={openSkuReportId} onBack={() => setOpenSkuReportId(null)} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default CatalogPage;